// FlowStageLane.tsx
import * as React from 'react';
import type { IndFlowAssignment } from '../../../MedicareProAPI/Entities/Ind/IndFlowAssignment';
import type { UserFlowStage } from '../../../MedicareProAPI/Entities/User/UserFlowStage';

export interface FlowStageLaneProps {
  stage: UserFlowStage;
  data: { individuals: IndFlowAssignment[]; offset: number; total: number };
  loading: boolean;

  draggingId: number | null;
  onCardDragStart: (payload: {
    assignmentId: number;
    individualId: number;
    currentStageId: number;
  }) => void;
  onCardDragEnd: () => void;

  onLaneDrop: (targetStageId: number) => void;
  onCardDropToStage: (
    payload: { assignmentId: number; individualId: number; currentStageId: number },
    targetStageId: number
  ) => void;

  onLoadMore: (stageId: number) => void;

  // controls
  onOpenMoveAll: (stageId: number) => void;
  onRemoveAll: (stageId: number) => void;
  onCloseAll: (stageId: number) => void;

  onOpenInd: (indId: number) => void;

  sortBy?: 'startAt' | 'indLastname';
  sortDir?: 'ASC' | 'DESC';
  onChangeSort?: (stageId: number, sortBy: 'startAt' | 'indLastname', dir: 'ASC' | 'DESC') => void;
  onShowAddToStageModalClick: (stageId: number) => void;
  onCloseOne?: (p: { stageId: number; assignmentId: number; indId: number }) => void;
  onSearch?: (search: string, stageId: number) => void;
}

export const FlowStageLane = React.memo(function FlowStageLane({
  stage,
  data,
  loading,
  draggingId,
  onCardDragStart,
  onCardDragEnd,
  onLaneDrop,
  onCardDropToStage,
  onLoadMore,
  onOpenMoveAll,
  onRemoveAll,
  onCloseAll,
  onOpenInd,
  sortBy = 'startAt',
  sortDir = 'DESC',
  onChangeSort,
  onShowAddToStageModalClick,
  onCloseOne,
  onSearch,
}: FlowStageLaneProps) {
  const total = data.total ?? data.individuals?.length ?? 0;
  const [now, setNow] = React.useState<number>(() => Date.now());
  const [laneCollapsed, setLaneCollapsed] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [isSearching, setIsSearching] = React.useState<boolean>(false);

  const formatDwY = (start?: Date | null): string => {
    if (!start) return '0d';
    const t = (start as any)?.getTime?.();
    if (!isFinite(t)) return '0d';

    const days = Math.floor((now - t) / (1000 * 60 * 60 * 24));
    if (days < 0) return '0d';

    if (days < 7) {
      return `${days}d`;
    }

    const weeks = Math.floor(days / 7);
    if (weeks < 52) {
      return `${weeks}w`;
    }

    const years = Math.floor(days / 365);
    return `${years}y`;
  };

  const collapsedWidth = 60;

  const laneStyle: React.CSSProperties = laneCollapsed
    ? {
        // collapsed: skinny, uniform
        flex: `0 0 ${collapsedWidth}px`,
        width: collapsedWidth,
        minWidth: collapsedWidth,
        maxWidth: collapsedWidth,
      }
    : {
        // expanded
        flex: '0 0 auto',
        minWidth: 250,
      };

  return (
    <div
      className="lane p-1 mx-0 "
      style={laneStyle}
      draggable
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onLaneDrop(stage.getId())}
    >
      <div
        className={`card m-2 border-${stage.getColor()} p-0 h-100 d-flex flex-column`}
        style={{ width: '100%' }}
      >
        {/* EXPANDED HEADER */}
        {!laneCollapsed && (
          <div className="card-header proCrdTitle d-flex align-items-center">
            <i
              className={`mr-2 far ${laneCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'} cp`}
              onClick={() => setLaneCollapsed(!laneCollapsed)}
            />
            <i className={`far ${stage.getIcon()} mr-2 text-${stage.getColor()}`} />
            <span className="text-truncate">{stage.getName()}</span>
            <span className="badge badge-slate ml-auto">{total}</span>
          </div>
        )}

        {/* COLLAPSED HEADER: vertical layout & full-height */}
        {laneCollapsed && (
          <div
            className="
                    card-header proCrdTitle
                    d-flex flex-column align-items-center justify-content-center text-center
                    flex-grow-1 pt-2 px-1
                "
          >
            <div>
              <i
                className={`far ${laneCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'} cp`}
                onClick={() => setLaneCollapsed(!laneCollapsed)}
              />
            </div>

            <div className="mt-1" style={{ fontSize: '13pt' }}>
              <i className={`far ${stage.getIcon()} text-${stage.getColor()}`} />
            </div>

            <div className="lane-title-wrapper mt-1">
              <span className="lane-title-rotated">{stage.getName()}</span>
            </div>

            <div className="mt-1">
              <span className="badge badge-slate">{total}</span>
            </div>
          </div>
        )}

        {!laneCollapsed && (
          <>
            <div className="ml-auto d-flex align-items-center">
              <div className="lane-controls">
                <div className="lane-controls__panel">
                  {total > 0 && (
                    <>
                      <select
                        className="form-control form-control-sm mr-1"
                        value={sortBy}
                        onChange={(e) =>
                          onChangeSort?.(stage.getId(), e.target.value as any, sortDir)
                        }
                      >
                        <option value="indLastname">Name</option>
                        <option value="startAt">Start</option>
                      </select>

                      <button
                        type="button"
                        className="btn btn-sm bg-contrast"
                        title={sortDir === 'ASC' ? 'Ascending' : 'Descending'}
                        onClick={() =>
                          onChangeSort?.(stage.getId(), sortBy, sortDir === 'ASC' ? 'DESC' : 'ASC')
                        }
                      >
                        <i className={`far fa-sort-amount-${sortDir === 'ASC' ? 'up' : 'down'}`} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm bg-contrast"
                        title="Add"
                        onClick={() => setIsSearching(!isSearching)}
                      >
                        <i className="far fa-search" />
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    className="btn btn-sm bg-contrast"
                    title="Add"
                    onClick={() => onShowAddToStageModalClick(stage.getId())}
                  >
                    <i className="far fa-plus" />
                  </button>

                  <div className="dropdown ml-2 mr-2">
                    <div className="cp dropdown-toggle small" data-toggle="dropdown">
                      <i className="far fa-ellipsis-h mr-1" />
                    </div>
                    <div className="dropdown-menu" style={{ fontSize: '9pt', zIndex: 1000 }}>
                      <div className="dropdown-item" onClick={() => onOpenMoveAll(stage.getId())}>
                        <i className="far fa-copy mr-2" /> Move all
                      </div>
                      <div className="dropdown-item" onClick={() => onCloseAll(stage.getId())}>
                        <i className="far fa-check-circle mr-2" /> Close all
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {isSearching && (
              <div className="card-body">
                <div className="input-group mb-3">
                  <input
                    type="text"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-control form-control-sm"
                    placeholder="Search..."
                    aria-label="Search..."
                    value={searchTerm}
                  />
                  <div className="input-group-append">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => onSearch(searchTerm, stage.getId())}
                      type="button"
                    >
                      <i className="far fa-search" />
                    </button>
                  </div>
                  {searchTerm !== '' && (
                    <div className="input-group-append">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          (onSearch('', stage.getId()), setSearchTerm(''), setIsSearching(false));
                        }}
                        type="button"
                      >
                        <i className="far fa-times" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div
              className="card-body"
              style={{ maxHeight: '60vh', overflowY: 'auto' }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                try {
                  const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
                  onCardDropToStage(payload, stage.getId());
                } catch {}
              }}
            >
              {(data.individuals ?? []).map((assignment) => {
                const ind = assignment.getInd();
                const isDragging = draggingId === ind?.getIndId();

                const startAt = assignment.getStartAt?.() as Date | null;
                const age = formatDwY(startAt);
                const startTitle = startAt ? startAt.toLocaleDateString() : 'N/A';

                return (
                  <div
                    key={assignment.getId()}
                    className={`kanban-ind-item lane-card bg-contrast rounded p-2 mb-2 calMess ${
                      isDragging ? 'dragging' : ''
                    }`}
                    draggable
                    onDragStart={(e) => {
                      e.stopPropagation();
                      onCardDragStart({
                        assignmentId: assignment.getId(),
                        individualId: ind?.getIndId(),
                        currentStageId: stage.getId(),
                      });
                      e.dataTransfer.setData(
                        'text/plain',
                        JSON.stringify({
                          assignmentId: assignment.getId(),
                          individualId: ind?.getIndId(),
                          currentStageId: stage.getId(),
                        })
                      );
                    }}
                    onDragEnd={onCardDragEnd}
                    onClick={() => ind?.getIndId() && onOpenInd(ind.getIndId())}
                  >
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <i className={`fal fa-user-circle mr-1 text-${stage.getColor()}`} />
                        {ind?.getIndName() || 'Unknown'}
                      </div>

                      <div className="lane-card__actions ml-1">
                        <span
                          className="badge bg-white ml-2 mr-1 text-dark"
                          title={`Started: ${startTitle}`}
                        >
                          {age}
                        </span>
                        <span
                          className="badge bg-white text-success"
                          title="Close (archive)"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCloseOne?.({
                              stageId: stage.getId(),
                              assignmentId: assignment.getId(),
                              indId: ind?.getIndId(),
                            });
                          }}
                        >
                          <i className="far fa-check" />
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {!loading && data.offset + (data.individuals?.length ?? 0) < total && (
                <button className="btn btn-link btn-sm" onClick={() => onLoadMore(stage.getId())}>
                  Load more
                </button>
              )}

              {loading && (
                <div className="text-center small">
                  <i className="far fa-spinner fa-spin" /> Loading…
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

/*
 <div className="dropdown-divider" />
                        <div className="dropdown-item text-danger" onClick={() => onRemoveAll(stage.getId())}>
                        <i className="far fa-trash mr-2" /> Remove all
                        </div>
*/
